import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Eye, Edit, Copy, XCircle, CheckCircle, Trash2, Lock, Info, Filter, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { sendSurveyNotifications } from "@/lib/sendSurveyNotifications";
import { useAuth } from "@/contexts/AuthContext";
import SurveyBuilder from "./SurveyBuilder";
import SurveyResponseViewer from "./SurveyResponseViewer";

interface Survey {
  id: string;
  title: string;
  status: string;
  is_public: boolean;
  public_slug: string | null;
  target_roles: string[];
  created_at: string;
  created_by: string;
  response_count?: number;
}

const SurveyManager = () => {
  const { roles, user } = useAuth();
  const isSuperadmin = roles.includes("superadmin");
  const isStudent = roles.includes("student");
  const canManage = !!user && roles.length > 0 && !isStudent;
  const canEditRow = (s: Survey) => isSuperadmin || (!!user && s.created_by === user.id);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [viewResponsesId, setViewResponsesId] = useState<string | null>(null);
  const [viewResponsesTitle, setViewResponsesTitle] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>("all"); // "all" | "public" | "internal_any" | role key
  const [statusFilter, setStatusFilter] = useState<string>("all"); // "all" | "draft" | "published" | "closed"

  const loadSurveys = async () => {
    setLoading(true);
    const { data } = await supabase.from("surveys").select("*").order("created_at", { ascending: false });
    if (data) {
      // Get response counts
      const ids = data.map(s => s.id);
      const counts: Record<string, number> = {};
      if (ids.length > 0) {
        const { data: responses } = await supabase
          .from("survey_responses")
          .select("survey_id")
          .in("survey_id", ids);
        (responses || []).forEach(r => {
          counts[r.survey_id] = (counts[r.survey_id] || 0) + 1;
        });
      }
      setSurveys(data.map(s => ({ ...s, response_count: counts[s.id] || 0 })));
    }
    setLoading(false);
  };

  useEffect(() => { loadSurveys(); }, []);

  const handleStatusChange = async (id: string, newStatus: string) => {
    const updateData: any = { status: newStatus };
    if (newStatus === "published") {
      updateData.published_at = new Date().toISOString();
    }
    const { error } = await supabase.from("surveys").update(updateData).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success(`Survey ${newStatus}`);

    // Send notifications when publishing
    if (newStatus === "published") {
      const survey = surveys.find(s => s.id === id);
      if (survey && survey.target_roles.length > 0) {
        sendSurveyNotifications(
          survey.title,
          survey.target_roles,
          survey.public_slug || survey.id,
          survey.is_public
        );
      }
    }

    loadSurveys();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    // Cascade delete handles questions and responses automatically
    const { error } = await supabase.from("surveys").delete().eq("id", deleteId);
    if (error) { toast.error(error.message); } else { toast.success("Survey deleted"); }
    setDeleteId(null);
    loadSurveys();
  };

  const copyLink = (survey: Survey) => {
    const base = window.location.origin;
    const url = survey.is_public && survey.public_slug
      ? `${base}/survey/${survey.public_slug}`
      : `${base}/survey/${survey.id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  // Reset selection when surveys list changes (e.g. after reload)
  useEffect(() => {
    setSelectedIds(prev => {
      const next = new Set<string>();
      surveys.forEach(s => { if (prev.has(s.id)) next.add(s.id); });
      return next;
    });
  }, [surveys]);

  // Distinct target roles across all surveys (for the filter dropdown)
  const availableRoles = Array.from(
    new Set(surveys.flatMap(s => s.target_roles || []))
  ).sort();

  const formatRoleLabel = (r: string) =>
    r.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

  const filteredSurveys = surveys.filter(s => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (roleFilter === "all") return true;
    if (roleFilter === "public") return s.is_public;
    if (roleFilter === "internal_any") return !s.is_public;
    return (s.target_roles || []).includes(roleFilter);
  });

  const filtersActive = roleFilter !== "all" || statusFilter !== "all";
  const clearFilters = () => { setRoleFilter("all"); setStatusFilter("all"); };

  const selectableSurveys = filteredSurveys.filter(canEditRow);
  const selectedSurveys = filteredSurveys.filter(s => selectedIds.has(s.id));
  const allSelectableSelected =
    selectableSurveys.length > 0 && selectableSurveys.every(s => selectedIds.has(s.id));
  const someSelected = selectedIds.size > 0 && !allSelectableSelected;

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(selectableSurveys.map(s => s.id)) : new Set());
  };

  const bulkPublishableIds = selectedSurveys
    .filter(s => s.status === "draft" || s.status === "closed")
    .map(s => s.id);
  const bulkClosableIds = selectedSurveys
    .filter(s => s.status === "published")
    .map(s => s.id);

  const runBulkStatusChange = async (newStatus: "published" | "closed", ids: string[]) => {
    if (ids.length === 0) {
      toast.info(
        newStatus === "published"
          ? "No selected surveys can be published (only draft or closed)."
          : "No selected surveys can be closed (only published)."
      );
      return;
    }
    setBulkBusy(true);

    // Validate publish-readiness for each selected survey
    if (newStatus === "published") {
      const { validateSurveyForPublish, formatPublishErrors } = await import("@/lib/surveyValidation");
      const { data: fullSurveys, error: sErr } = await supabase
        .from("surveys")
        .select("id, title, description, is_public, public_slug, target_roles, inquiry_phone, released_by")
        .in("id", ids);
      if (sErr) { toast.error(sErr.message); setBulkBusy(false); return; }
      const { data: allQs, error: qErr } = await supabase
        .from("survey_questions")
        .select("survey_id, question_text, question_type, options, is_required, sort_order")
        .in("survey_id", ids)
        .order("sort_order");
      if (qErr) { toast.error(qErr.message); setBulkBusy(false); return; }

      const qsBySurvey = new Map<string, any[]>();
      (allQs || []).forEach(q => {
        const arr = qsBySurvey.get(q.survey_id) || [];
        arr.push(q);
        qsBySurvey.set(q.survey_id, arr);
      });

      const blocking: string[] = [];
      for (const s of fullSurveys || []) {
        const result = validateSurveyForPublish({
          title: s.title,
          description: (s as any).description,
          is_public: s.is_public,
          public_slug: s.public_slug,
          target_roles: s.target_roles || [],
          inquiry_phone: (s as any).inquiry_phone,
          released_by: (s as any).released_by,
          questions: qsBySurvey.get(s.id) || [],
        });
        if (result.ok === false) {
          blocking.push(`"${s.title}": ${result.errors[0]}`);
        }
      }
      if (blocking.length > 0) {
        toast.error(formatPublishErrors(blocking, 5).replace("Cannot publish:", "Cannot publish these surveys:"), { duration: 9000 });
        setBulkBusy(false);
        return;
      }
    }

    const updateData: any = { status: newStatus };
    if (newStatus === "published") updateData.published_at = new Date().toISOString();

    const { error } = await supabase.from("surveys").update(updateData).in("id", ids);
    if (error) {
      toast.error(error.message);
      setBulkBusy(false);
      return;
    }
    toast.success(`${ids.length} survey${ids.length === 1 ? "" : "s"} ${newStatus}`);

    if (newStatus === "published") {
      // Fire notifications for each survey we just published
      const justPublished = surveys.filter(s => ids.includes(s.id));
      for (const survey of justPublished) {
        if (survey.target_roles.length > 0) {
          sendSurveyNotifications(
            survey.title,
            survey.target_roles,
            survey.public_slug || survey.id,
            survey.is_public
          );
        }
      }
    }

    setSelectedIds(new Set());
    setBulkBusy(false);
    loadSurveys();
  };
  const statusColor = (status: string) => {
    if (status === "published") return "default";
    if (status === "closed") return "secondary";
    return "outline";
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <span>Create and manage surveys/forms</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex" aria-label="Permissions info">
                <Info className="h-3.5 w-3.5 text-muted-foreground/70 hover:text-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs text-xs">
              {isSuperadmin
                ? "You're a Superadmin: you can edit, publish, and delete any survey."
                : canManage
                  ? "You can create surveys and manage only the ones you created. Other surveys are read-only."
                  : isStudent
                    ? "Students can respond to surveys but cannot create or edit them."
                    : "Sign in with a staff role to create or edit surveys."}
            </TooltipContent>
          </Tooltip>
        </div>
        {canManage && (
          <Button size="sm" onClick={() => { setEditId(null); setBuilderOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" /> New Survey
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Filter className="h-3.5 w-3.5" />
          <span>Filter</span>
        </div>
        <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v)}>
          <SelectTrigger className="h-8 w-[200px] text-xs">
            <SelectValue placeholder="All audiences" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All audiences</SelectItem>
            <SelectItem value="public">Public link surveys</SelectItem>
            <SelectItem value="internal_any">Internal (any role)</SelectItem>
            {availableRoles.length > 0 && (
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">By role</div>
            )}
            {availableRoles.map(r => (
              <SelectItem key={r} value={r}>{formatRoleLabel(r)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue placeholder="Any status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        {filtersActive && (
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={clearFilters}>
            <X className="h-3.5 w-3.5 mr-1" /> Clear filters
          </Button>
        )}
        <span className="ml-auto text-muted-foreground">
          {filteredSurveys.length} of {surveys.length} survey{surveys.length === 1 ? "" : "s"}
        </span>
      </div>

      {canManage && selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs">
          <span className="font-medium">
            {selectedIds.size} selected
            {bulkPublishableIds.length + bulkClosableIds.length < selectedIds.size && (
              <span className="text-muted-foreground font-normal">
                {" "}({bulkPublishableIds.length} publishable, {bulkClosableIds.length} closable)
              </span>
            )}
          </span>
          <div className="flex gap-1.5 ml-auto">
            <Button
              size="sm"
              variant="outline"
              disabled={bulkBusy || bulkPublishableIds.length === 0}
              onClick={() => runBulkStatusChange("published", bulkPublishableIds)}
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1 text-green-600" />
              Publish ({bulkPublishableIds.length})
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={bulkBusy || bulkClosableIds.length === 0}
              onClick={() => runBulkStatusChange("closed", bulkClosableIds)}
            >
              <XCircle className="h-3.5 w-3.5 mr-1 text-destructive" />
              Close ({bulkClosableIds.length})
            </Button>
            <Button size="sm" variant="ghost" disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : surveys.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No surveys yet. Create your first survey to get started.
          </CardContent>
        </Card>
      ) : filteredSurveys.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground space-y-2">
            <p>No surveys match the current filters.</p>
            <Button size="sm" variant="outline" onClick={clearFilters}>
              <X className="h-3.5 w-3.5 mr-1" /> Clear filters
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {canManage && (
                  <TableHead className="w-8">
                    <Checkbox
                      checked={allSelectableSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={(v) => toggleSelectAll(!!v)}
                      disabled={selectableSurveys.length === 0}
                      aria-label="Select all editable surveys"
                    />
                  </TableHead>
                )}
                <TableHead className="text-xs">Title</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Audience</TableHead>
                <TableHead className="text-xs">Responses</TableHead>
                <TableHead className="text-xs">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSurveys.map(s => (
                <TableRow key={s.id} data-state={selectedIds.has(s.id) ? "selected" : undefined}>
                  {canManage && (
                    <TableCell className="w-8">
                      {canEditRow(s) ? (
                        <Checkbox
                          checked={selectedIds.has(s.id)}
                          onCheckedChange={(v) => toggleSelect(s.id, !!v)}
                          aria-label={`Select ${s.title}`}
                        />
                      ) : (
                        <span className="inline-block w-4 h-4" aria-hidden />
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-xs font-medium">{s.title}</TableCell>
                  <TableCell>
                    <Badge variant={statusColor(s.status)} className="text-[10px]">{s.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">{s.is_public ? "Public" : "Internal"}</TableCell>
                  <TableCell className="text-xs">
                    {s.is_public && (!s.target_roles || s.target_roles.length === 0) ? (
                      <span className="text-muted-foreground italic">Anyone with link</span>
                    ) : !s.target_roles || s.target_roles.length === 0 ? (
                      <span className="text-muted-foreground italic">No roles</span>
                    ) : (
                      <div className="flex flex-wrap gap-1 max-w-[220px]">
                        {s.target_roles.slice(0, 3).map(r => (
                          <Badge
                            key={r}
                            variant="outline"
                            className="text-[10px] cursor-pointer"
                            onClick={() => setRoleFilter(r)}
                            title={`Filter to ${formatRoleLabel(r)}`}
                          >
                            {formatRoleLabel(r)}
                          </Badge>
                        ))}
                        {s.target_roles.length > 3 && (
                          <Badge variant="secondary" className="text-[10px]" title={s.target_roles.slice(3).map(formatRoleLabel).join(", ")}>
                            +{s.target_roles.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">{s.response_count}</TableCell>
                  <TableCell>
                    {(() => {
                      const owns = canEditRow(s);
                      const reason = isSuperadmin
                        ? "Superadmin: full access to every survey."
                        : owns
                          ? "You created this survey, so you can edit, publish, and delete it."
                          : !canManage
                            ? (isStudent ? "Students can only view and respond to surveys." : "Sign in with a staff role to manage surveys.")
                            : "Only the survey's creator (or a Superadmin) can edit, publish, or delete it.";
                      return (
                        <div className="flex gap-1 items-center">
                          {owns && (
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditId(s.id); setBuilderOpen(true); }} title={reason}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {owns && (s.status === "draft" || s.status === "closed") && (
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleStatusChange(s.id, "published")} title="Publish — sends notifications to targeted roles.">
                              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                            </Button>
                          )}
                          {s.status === "published" && (
                            <>
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyLink(s)} title="Copy shareable link.">
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              {owns && (
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleStatusChange(s.id, "closed")} title="Close survey — stops accepting responses.">
                                  <XCircle className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              )}
                            </>
                          )}
                          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setViewResponsesId(s.id); setViewResponsesTitle(s.title); }} title="View responses.">
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          {owns && (
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setDeleteId(s.id)} title="Delete survey (permanent).">
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          )}
                          {!owns && canManage && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center text-muted-foreground/70 ml-0.5" aria-label="Read-only">
                                  <Lock className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="left" className="max-w-xs text-xs">
                                {reason}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <SurveyBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        editSurveyId={editId}
        onSaved={loadSurveys}
      />

      {viewResponsesId && (
        <SurveyResponseViewer
          open={!!viewResponsesId}
          onOpenChange={(open) => { if (!open) setViewResponsesId(null); }}
          surveyId={viewResponsesId}
          surveyTitle={viewResponsesTitle}
        />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(open) => { if (!open) setDeleteId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Survey</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the survey, all its questions, and all responses. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
};

export default SurveyManager;
